import React, { useReducer, useEffect } from 'react';

//Imports to the axios instance to make API calls
import API from '../../services/api';

import AuthContext from './AuthContext';
import AuthReducer from './AuthReducer';

import { useTranslation } from 'react-i18next';

//notifications
import Notifications, { ERROR_NOTIFICATION } from '../../helpers/notifications'

//These are the actions that can be performed in this context
import { SIGNIN, SIGNOUT, SELECT_INSTITUTION, UPDATE_FIRST_TIME } from '../types';

const AuthState = (props) => {
    //Initial state of the reducer
    const initialState = {
        user: null,
        authorizedInstitutions: null,
        currentInstitutionIndex: -1,
        baseUrlLogoInstitutions: ''
    };

    /*
    * Creation of the reducer with the function that makes the updates depending on a type and the initial state. 
    * Retorts the state and the dispach function, which is the one we must call to update the state, 
    * passing the type and the payload as an object. 
    */
    const [state, dispatch] = useReducer(AuthReducer, initialState);
    const { t } = useTranslation('common');

    useEffect(() => {
        // * User is retrieved if it exists from session storage
        get_user_from_session()
    }, [])

    /*
      * This function is in charge of using the usr and pw to try to give access to the user.
      * If the user has access, a call is made to the cb function, to redirect the user to where it should be.
    */
    const login = async (usr, pw, cb) => {
        try {

            //http requetes to api
            const res = await API.post('/Security/Login', { usr, pw });

            if (res.data.code !== 100) {
                Notifications.notify(t(`login.${res.data.code}`), ERROR_NOTIFICATION)
                return;
            }

            const { authorizedInstitutions, currentInstitutionIndex, baseUrlLogoInstitutions, ...user } = res.data.data

            const data = { authorizedInstitutions, currentInstitutionIndex, user, baseUrlLogoInstitutions }

            //save the user in the session storage
            sessionStorage.setItem('user_data', JSON.stringify(data))

            //update state of reducer
            dispatch({ type: SIGNIN, payload: data });

            //redirect the user
            cb();

        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error);
        }
    };

    /*
      * function that is in charge of removing the user's information, in order to log the user out of the system.
    */
    const logout = () => {
        dispatch({ type: SIGNOUT, payload: null });
        sessionStorage.removeItem('user_data')
    };

    /*
      * function that gets all the user information if it exists in the session storage
    */
    const get_user_from_session = () => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));
        dispatch({ type: SIGNIN, payload: userDataObj });
        return userDataObj
    }

    /*
      * function that returns true if the user has already entered the system through the login.
    */
    const is_login = () => {
        return state.user || sessionStorage.getItem('user_data');
    }

    /*
      * function that returns true if the user has already selected an institution
    */
    const is_selected_institution = () => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));
        return userDataObj ? state.currentInstitutionIndex !== -1 || userDataObj.currentInstitutionIndex !== -1 : false;
    }

    /*
      * function that returns true if it is the first time the user logs in to the system
    */
    const is_firstime = () => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));
        return userDataObj ? state.isFirstTime || userDataObj.user.isFirstTime : false;
    }
    /*
      *función that change the currentInstitutionIndex
    */
    const select_institution = (indexID) => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));
        sessionStorage.setItem('user_data', JSON.stringify({ ...userDataObj, currentInstitutionIndex: indexID }))
        dispatch({ type: SELECT_INSTITUTION, payload: indexID })
    }
    /*
      * function that updates the first_time variable, when the user enters the system and makes the password change.
    */
    const update_first_time = () => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));
        sessionStorage.setItem('user_data', JSON.stringify({ ...userDataObj, user: { ...userDataObj.user, isFirstTime: false } }))
        dispatch({ type: UPDATE_FIRST_TIME, payload: true })
    }

    const extAuth = async (urlparams, cb) => {
        try {

            //http requetes to api
            // const res = await API.get(`/Security/Extauth?ciid=${ciid}&cuid=${cuid}&ts=${ts}&sk=${atob(sk)}`);
            const res = await API.get(`/Security/Extauth${urlparams}`);

            if (res.data.code !== 100) {
                Notifications.notify(t(`login.${res.data.code}`), ERROR_NOTIFICATION)
                cb('/Login');
                return;
            }

            const { authorizedInstitutions, currentInstitutionIndex, baseUrlLogoInstitutions, ...user } = res.data.data

            const data = { authorizedInstitutions, currentInstitutionIndex, user, baseUrlLogoInstitutions }

            //save the user in the session storage
            sessionStorage.setItem('user_data', JSON.stringify(data))

            //update state of reducer
            dispatch({ type: SIGNIN, payload: data });

            //redirect the user
            cb('/');

        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error);
        }
    };

    const get_currentInstitution = () => {
        const userDataObj = JSON.parse(sessionStorage.getItem('user_data'));

        if (userDataObj?.authorizedInstitutions) {
            const institution = userDataObj.authorizedInstitutions[userDataObj.currentInstitutionIndex];
            return institution;
        }
        return {}
    }

    /*
      * the context provider is the one that allows everything that is rendered inside the AuthState
      * to access the things specified in value 
     */
    return (
        <AuthContext.Provider

            value={{
                ...state,
                login,
                logout,
                is_login,
                is_selected_institution,
                is_firstime,
                select_institution,
                get_currentInstitution,
                update_first_time,
                extAuth,
                get_user_from_session
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthState;
